import React, { useState } from 'react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { Gift, ChevronDown, ChevronUp } from 'lucide-react';

export function FreeAccountsPanel({ freeMembers = 0, freeAccounts = [] }) {
  const [showList, setShowList] = useState(false);

  if (freeAccounts.length === 0 && freeMembers === 0) return null;

  return (
    <div className="border border-purple-200 rounded-sm bg-white mb-8" data-testid="free-accounts-panel">
      <button
        onClick={() => setShowList(!showList)}
        className="w-full flex items-center justify-between p-4 hover:bg-purple-50/50 transition-colors"
        data-testid="free-accounts-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-purple-50 rounded-sm flex items-center justify-center">
            <Gift className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-slate-900">Free Accounts Usage</p>
            <p className="text-xs text-slate-500">{freeMembers} user{freeMembers !== 1 ? 's' : ''} with free membership</p>
          </div>
        </div>
        {showList ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {showList && (
        <div className="border-t border-purple-100">
          {freeAccounts.length === 0 ? (
            <p className="p-4 text-sm text-slate-400 text-center">No free accounts</p>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                <div className="col-span-3">User</div>
                <div className="col-span-1 text-center">Role</div>
                <div className="col-span-2 text-center">Leads Created</div>
                <div className="col-span-2 text-center">Leads Shared</div>
                <div className="col-span-2 text-center">Actions (30d)</div>
                <div className="col-span-2 text-right">Last Active</div>
              </div>
              <div className="divide-y divide-slate-100">
                {freeAccounts.map(account => (
                  <div key={account.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`free-account-${account.id}`}>
                    <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                      <MemberAvatar userId={account.id} name={account.name} size="sm" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{account.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{account.email}</p>
                      </div>
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-sm bg-purple-100 text-purple-700 capitalize">{account.role}</span>
                    </div>
                    <div className="col-span-2 text-center" data-testid={`free-leads-created-${account.id}`}>
                      <span className="text-lg font-bold text-slate-900">{account.leads_created || 0}</span>
                      <p className="text-[10px] text-slate-400 sm:hidden">leads created</p>
                    </div>
                    <div className="col-span-2 text-center" data-testid={`free-leads-shared-${account.id}`}>
                      <span className="text-lg font-bold text-slate-900">{account.leads_shared || 0}</span>
                      <p className="text-[10px] text-slate-400 sm:hidden">shared with</p>
                    </div>
                    <div className="col-span-2 text-center" data-testid={`free-actions-${account.id}`}>
                      <span className={`text-lg font-bold ${(account.recent_actions || 0) > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                        {account.recent_actions || 0}
                      </span>
                      <p className="text-[10px] text-slate-400 sm:hidden">actions (30d)</p>
                    </div>
                    <div className="col-span-2 text-right">
                      {account.last_active ? (
                        <div>
                          <p className="text-xs text-slate-600">{new Date(account.last_active).toLocaleDateString()}</p>
                          <p className="text-[10px] text-slate-400">{new Date(account.last_active).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-300">Never</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
